import {createElement} from 'react';

import classNames from 'classnames';

import {TypographyClasses} from './constants';

import type {ComponentPropsWithoutRef, ElementType, ReactElement} from 'react';


export type TypographyProps<T extends ElementType = 'div'> = {
    /**
     * The element type of the Typography component. This can be any valid HTML tag
     */
    as?: T,

    /**
     * Style variant of the component
     * */
    variant?: keyof typeof TypographyClasses,

    /**
     * Font weight of the component
     * */
    fontWeight?: 'normal' | 'semi-bold' | 'bold',
} & ComponentPropsWithoutRef<T>;

/**
 * @description The `Typography` component is a versatile typography component
 * that can render any valid HTML tag with various typography styles.
 *
 * ## Features
 * - Supports different typography variants, font weights.
 * - Can render any valid HTML tag.
 * - Supports any valid HTML attributes.
 *
 * ## Accessibility
 * - The rendered HTML tag should follow the accessibility guidelines for that element.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/core-typography--documentation}
 */
export function Typography<T extends ElementType = 'div'>(props: TypographyProps<T>): ReactElement<unknown> {
    const {
        as: Slug = 'div',
        variant = 'body-level-3',
        fontWeight,
        className,
        children,
        ...otherProps
    } = props;

    return createElement(Slug, {
        ...otherProps,
        className: classNames(
            className,
            fontWeight ? `font-weight-${fontWeight}` : undefined,
            TypographyClasses[variant],
        ),
    }, children);
}
